<template>
  <v-dialog v-model="localValue" scrollable width="800" lazy persistent>
    <v-card>
      <v-toolbar color="primary" dense dark>
        <v-toolbar-title>
          {{
            $t(`titles.global.${role ? 'updateModel' : 'newModel'}`, {
              field: $tc('models.role'),
            })
          }}
        </v-toolbar-title>
        <v-spacer />
        <v-toolbar-items>
          <v-btn dark icon @click="cancel">
            <v-icon>close</v-icon>
          </v-btn>
        </v-toolbar-items>
      </v-toolbar>
      <v-card-text class="py-0">
        <v-container fluid>
          <v-layout row wrap>
            <v-flex md6 sm6 xs12 px-2>
              <v-autocomplete
                v-model="localRole.roleId"
                v-validate="'required'"
                :items="roleList"
                :label="
                  $t('action.actions.select', { field: $tc('models.role') }) +
                  ' (*)'
                "
                clearable
                :data-vv-name="$tc('models.role')"
                :error-messages="errors.collect($tc('models.role'))"
                @focus="(e) => e.target.click()"
                @change="localRole.objectId = ''"
              />
            </v-flex>
            <template v-if="!isTypeRole(localRole.roleId, ['all', 'support'])">
              <template
                v-if="isTypeRole(localRole.roleId, ['trainer', 'player'])"
              >
                <v-flex md7 sm6 xs12 px-2>
                  <category-menu
                    v-model="categoryId"
                    clearable
                    :label="$tc('models.category')"
                  />
                </v-flex>
                <v-flex md7 sm6 xs12 px-2>
                  <team-field
                    v-model="localRole.objectId"
                    v-validate="{
                      required: isTypeRole(localRole.roleId, [
                        'trainer',
                        'player',
                      ]),
                    }"
                    :category-id="categoryId"
                    :entity-id="entityId"
                    :label="$tc('models.team') + ' (*)'"
                    :data-vv-name="$tc('models.team')"
                    :error-messages="errors.collect($tc('models.team'))"
                  />
                </v-flex>
              </template>
              <template
                v-else-if="
                  isTypeRole(localRole.roleId, ['president', 'coordinator'])
                "
              >
                <v-flex md7 sm6 xs12 px-2>
                  <entity-field
                    v-model="localRole.objectId"
                    v-validate="{
                      required: isTypeRole(localRole.roleId, [
                        'president',
                        'coordinator',
                      ]),
                    }"
                    :label="$tc('models.entity') + ' (*)'"
                    :data-vv-name="$tc('models.entity')"
                    :error-messages="errors.collect($tc('models.entity'))"
                  />
                </v-flex>
              </template>
              <template
                v-else-if="
                  isTypeRole(localRole.roleId, 'coordinatorTournament')
                "
              >
                <v-flex md7 sm6 xs12 px-2>
                  <tournament-field
                    v-model="localRole.objectId"
                    v-validate="{
                      required: isTypeRole(
                        localRole.roleId,
                        'coordinatorTournament'
                      ),
                    }"
                    :label="$tc('models.tournament') + ' (*)'"
                    :data-vv-name="$tc('models.tournament')"
                    :error-messages="errors.collect($tc('models.tournament'))"
                  />
                </v-flex>
              </template>
              <template v-else-if="isTypeRole(localRole.roleId, 'familiar')">
                <v-flex md7 sm6 xs12 px-2>
                  <user-field
                    v-model="localRole.objectId"
                    v-validate="{
                      required: isTypeRole(localRole.roleId, 'familiar'),
                    }"
                    populate-entity
                    populate-team
                    type-role="player"
                    :label="$tc('titles.global.player') + ' (*)'"
                    :data-vv-name="$tc('titles.global.player')"
                    :error-messages="
                      errors.collect($tc('titles.global.player'))
                    "
                  />
                </v-flex>
              </template>
            </template>
          </v-layout>
        </v-container>
      </v-card-text>
      <v-divider />
      <v-card-actions>
        <template>
          <span style="color: red">(*) &nbsp;</span>
          <span>{{ $t('action.messages.fieldRequired') }}</span>
        </template>
      </v-card-actions>
      <v-card-actions>
        <v-spacer />
        <meta-button type="reset" :disabled="loading" @click="clear" />
        <meta-button type="cancel" :disabled="loading" @click="cancel" />
        <meta-button
          :type="role ? 'update' : 'create'"
          :loading="loading"
          @click="submit"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import CategoryMenu from '@/components/management/categories/CategoryMenu'
import EntityField from '@/components/widgets/Input/EntityField'
import TeamField from '@/components/widgets/Input/TeamField'
import TournamentField from '@/components/widgets/Input/TournamentField'
import UserField from '@/components/widgets/Input/UserField'

export default {
  name: 'DialogUserRole',
  components: {
    TeamField,
    EntityField,
    TournamentField,
    UserField,
    CategoryMenu,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
      required: true,
    },
    userId: {
      type: String,
      default: '',
      required: true,
    },
    role: null,
    entityId: {
      type: String,
    },
    typeRole: {
      type: String,
    },
  },
  data() {
    return {
      localRole: { roleId: '', objectId: '' },
      categoryId: '',
      loading: false,
    }
  },
  watch: {
    value(val) {
      if (val) {
        if (this.role) {
          this.localRole.objectId = this.role.objectId[0]
          this.localRole.roleId = this.role.roleId
        }
      } else this.clear()
    },
  },
  computed: {
    ...mapGetters({
      roleSelectList: 'roles/selectListFiltered',
    }),
    roleList() {
      return this.roleSelectList.filter((role) =>
        !this.role && this.typeRole ? role.type == this.typeRole : true
      )
    },
    localValue: {
      get() {
        return this.value
      },
      set(value) {
        this.$emit('input', value)
      },
    },
  },
  methods: {
    ...mapActions({
      assignRole: 'users/assignRole',
    }),
    roleById(id) {
      return this.roleSelectList.find((item) => item.value === id)
    },
    isTypeRole(id, type) {
      const role = this.roleById(id)
      if (!role) return false
      if (Array.isArray(type)) return type.includes(role.type)
      else return role.type === type
    },
    submit() {
      this.validate()
        .then((valid) => {
          if (!valid) return

          let { roleId, objectId } = this.localRole
          let payload = {
            roleId,
            objectId,
            userId: this.userId,
            confirm: false,
          }
          this.loading = true
          this.assignRole(payload)
            .then(() => {
              if (this.role) this.$emit('unrole', this.role._id)
              this.cancel()
            })
            .finally(() => {
              this.loading = false
            })
        })
        .catch(() => {})
    },

    clear() {
      this.localRole = { roleId: '', objectId: [] }
      this.categoryId = ''
      this.$validator.pause()
      this.nextTick(() => {
        this.$validator.reset()
        this.$validator.resume()
      })
    },
    cancel() {
      this.localValue = false
      this.clear()
    },
  },
}
</script>
